import React, { useState, useEffect, useCallback } from 'react';
import { api, formatApiError } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { DownloadSimple, Bank, MagicWand, Eye } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

export const ClientBankStatementsTab = ({ clientId }) => {
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewName, setPreviewName] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.get(`/clients/${clientId}/all-uploads`);
      setStatements(data.bank_statements || []);
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleScan = async (statementId) => {
    try {
      toast.info('Scanning bank statement...');
      await api.post(`/bank-statements/${statementId}/scan`);
      toast.success('Bank statement scanned — transactions extracted');
      fetchData();
    } catch (error) { toast.error(formatApiError(error)); }
  };

  const handlePreview = async (stmt) => {
    try {
      const response = await api.get(`/files/${stmt.file_path}`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      setPreviewUrl(url);
      setPreviewName(stmt.original_filename || 'Bank Statement');
    } catch { toast.error('Failed to load preview'); }
  };

  const handleDownload = async (stmt) => {
    try {
      const response = await api.get(`/files/${stmt.file_path}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a'); a.href = url; a.download = stmt.original_filename || 'bank_statement'; document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download'); }
  };

  if (loading) return <div className="flex items-center justify-center h-32"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <>
      <Card className="border border-border">
        <CardHeader className="pb-3">
          <CardTitle className="heading-4 flex items-center gap-2"><Bank className="w-5 h-5" /> Bank Statements <Badge variant="outline" className="ml-2">{statements.length}</Badge></CardTitle>
          <CardDescription>All bank statements — uploaded by admin or client</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {statements.length === 0 ? (
            <div className="text-center py-8"><p className="text-muted-foreground">No bank statements yet</p></div>
          ) : (
            <div className="divide-y">
              {statements.map((stmt) => (
                <div key={stmt.id} className="flex items-center justify-between px-4 py-3" data-testid={`statement-row-${stmt.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{stmt.original_filename || 'Bank Statement'}</p>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      {stmt.created_at && <span className="text-xs text-muted-foreground">{new Date(stmt.created_at).toLocaleDateString()}</span>}
                      {stmt.uploaded_by === 'client' && <Badge variant="secondary" className="text-[10px] py-0 bg-blue-50 text-blue-600">Client Upload</Badge>}
                      {stmt.status && <Badge className={`text-[10px] py-0 ${stmt.status === 'processed' ? 'bg-green-50 text-green-600' : 'bg-amber-50 text-amber-600'}`}>{stmt.status}</Badge>}
                      {stmt.transactions_count > 0 && <span className="text-xs text-muted-foreground">{stmt.transactions_count} transactions</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {stmt.status === 'pending' && stmt.file_path && (
                      <Button variant="outline" size="sm" onClick={() => handleScan(stmt.id)} data-testid={`scan-stmt-${stmt.id}`}><MagicWand className="w-3.5 h-3.5 mr-1" /> Scan</Button>
                    )}
                    {stmt.file_path && (
                      <Button variant="outline" size="sm" onClick={() => handlePreview(stmt)} data-testid={`preview-stmt-${stmt.id}`}><Eye className="w-3.5 h-3.5 mr-1" /> Preview</Button>
                    )}
                    {stmt.file_path && (
                      <Button variant="outline" size="sm" onClick={() => handleDownload(stmt)} data-testid={`download-stmt-${stmt.id}`}><DownloadSimple className="w-3.5 h-3.5 mr-1" /> Download</Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview Dialog */}
      <Dialog open={!!previewUrl} onOpenChange={(open) => { if (!open) { if (previewUrl) window.URL.revokeObjectURL(previewUrl); setPreviewUrl(null); } }}>
        <DialogContent className="max-w-4xl max-h-[92vh] p-0">
          <DialogHeader className="px-6 pt-6 pb-2">
            <DialogTitle data-testid="bank-statement-preview-title">{previewName}</DialogTitle>
          </DialogHeader>
          <div className="px-6 pb-6">
            {previewUrl && (
              <iframe
                src={previewUrl}
                className="w-full rounded-md border"
                style={{ height: '70vh' }}
                title="Bank Statement Preview"
                data-testid="bank-statement-preview-iframe"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
